import { useAuth0 } from '@auth0/auth0-react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useFilterFromType from 'components/Dashboard/StakeHolders/StakeholderPage/useFilterFromType';
import { store } from 'components/Store';
import { useContext } from 'react';
import { useParams } from 'react-router';
import ChangeSearchQuery from 'components/Dashboard/utilities/changeSearchQuery';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import getAppSite from 'utils/getAppSite';

const useStakeholderContentRelated = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan, search } = state;

  const { contentResults } = useContentResults();
  const { list, activeSearch } = search;
  const { enableStakeholders } = activePlan;

  const resourcesToShow = 3;
  const contentRelatedpageSize = 50;
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';

  const params = useParams();
  const { id, contentType } = params;
  const { filterFromType, changeContentType } = useFilterFromType({ stakeholderPage: true });

  const { isAuthenticated } = useAuth0();
  const { generalApiCall } = useGeneralApiCall();

  const { Stakeholders } = contentResults ?? {};
  const { contentRelated } = Stakeholders ? Stakeholders : {};
  const stakeholderSearchCondition = (isAuthenticated && enableStakeholders) || !isAuthenticated;

  const contentTypeFilters = () => {
    let contentTypeToUse = changeContentType(contentType);
    let typeToApply = createTypeToApply({ contentTypeToUse });
    let filtersProps;
    let additionalFilters;
    let typeToUse;
    if (contentType === 'early-day-motions-as-primary-sponsor-or-supporter') {
      filtersProps = [
        {
          field: 'contactIds',
          value: `${id}`,
          operator: 'str_eq',
        },
        {
          field: 'contentType',
          operator: 'str_eq',
          value: 'Edm',
        },
      ];
    } else if (contentType === 'answers-to-written-parliamentary-questions') {
      filtersProps = filterFromType(changeContentType(contentType));
    } else if (contentTypeToUse === 'WrittenQuestion') {
      typeToUse = 'ParliamentaryRecord';
    }
    return { contentTypeToUse, typeToApply, filtersProps, additionalFilters, typeToUse };
  };

  const createTypeToApply = ({ contentTypeToUse }) => {
    if (contentTypeToUse) {
      if (contentTypeToUse === 'EdmPrimaryOrSupported' || contentTypeToUse === 'WrittenAnswer') {
        return 'ParliamentaryRecord';
      }
      return contentTypeToUse;
    }
  };

  const relatedContentCall = async ({
    content,
    pageNumber,
    topic,
    freeSearch,
    filtersProps,
    sourceProp,
    activeSearchResultsConditional,
  }) => {
    let contentRelated;
    let commonProperties = {
      freeSearch,
      content,
      topic,
      filtersProps,
      sourceProp,
    };

    if (contentType === undefined) {
      if (getAppSite() === 'usa') {
        let ParliamentaryRecord = await callRelatedContent({
          ...commonProperties,
          filtersProps: [
            { field: 'contactIds', value: id, operator: 'STR_EQ' },
            { field: 'contentType', value: 'CREC', operator: 'STR_EQ' },
          ],
          type: 'ParliamentaryRecord',
        });
        contentRelated = { ParliamentaryRecord };
      } else {
        contentRelated = {};

        const tweetCondition = stakeholderSearchCondition && showTweets;

        if (activeSearchResultsConditional) {
          // UI order when key updates are rendered later:
          // ParliamentaryRecord -> WrittenAnswer -> WrittenQuestion -> EdmPrimaryOrSupported -> tweet -> KeyUpdate
          let ParliamentaryRecord = await callRelatedContent({
            ...commonProperties,
            type: 'ParliamentaryRecord',
          });
          contentRelated.ParliamentaryRecord = ParliamentaryRecord;

          await callForWrittenQuestionAndAnswer({ commonProperties, contentRelated });

          let EdmPrimaryOrSupported = await callRelatedContent({
            ...commonProperties,
            type: 'ParliamentaryRecord',
            filtersProps: [
              {
                field: 'contactIds',
                value: `${id}`,
                operator: 'str_eq',
              },
              {
                field: 'contentType',
                operator: 'str_eq',
                value: 'Edm',
              },
            ],
          });
          contentRelated.EdmPrimaryOrSupported = EdmPrimaryOrSupported;

          let tweet = tweetCondition ? await callRelatedContent({ ...commonProperties, type: 'tweet' }) : [];
          if (tweetCondition) {
            contentRelated.tweet = tweet;
          }

          let KeyUpdate = await callRelatedContent({ ...commonProperties, type: 'KeyUpdate' });
          contentRelated.KeyUpdate = KeyUpdate;
        } else {
          // UI order when key updates are rendered earlier:
          // KeyUpdate -> ParliamentaryRecord -> WrittenAnswer -> WrittenQuestion -> EdmPrimaryOrSupported -> tweet
          let KeyUpdate = await callRelatedContent({ ...commonProperties, type: 'KeyUpdate' });
          contentRelated.KeyUpdate = KeyUpdate;

          let ParliamentaryRecord = await callRelatedContent({
            ...commonProperties,
            type: 'ParliamentaryRecord',
          });
          contentRelated.ParliamentaryRecord = ParliamentaryRecord;

          await callForWrittenQuestionAndAnswer({ commonProperties, contentRelated });

          let EdmPrimaryOrSupported = await callRelatedContent({
            ...commonProperties,
            type: 'ParliamentaryRecord',
            filtersProps: [
              {
                field: 'contactIds',
                value: `${id}`,
                operator: 'str_eq',
              },
              {
                field: 'contentType',
                operator: 'str_eq',
                value: 'Edm',
              },
            ],
          });
          contentRelated.EdmPrimaryOrSupported = EdmPrimaryOrSupported;

          let tweet = tweetCondition ? await callRelatedContent({ ...commonProperties, type: 'tweet' }) : [];
          if (tweetCondition) {
            contentRelated.tweet = tweet;
          }
        }
      }
    } else {
      const { typeToApply, filtersProps, additionalFilters, typeToUse } = contentTypeFilters();
      let contentRelatedFromType = await callRelatedContent({
        ...commonProperties,
        type: typeToApply,
        pageNumber,
        filtersProps,
        additionalFilters,
        typeToUse,
        sourceProp,
      });
      contentRelated = { [changeContentType(contentType)]: contentRelatedFromType };
    }

    return contentRelated;
  };

  const callForWrittenQuestionAndAnswer = async ({ commonProperties, contentRelated }) => {
    if (contentRelated) {
      contentRelated.WrittenQuestion = await callRelatedContent({
        ...commonProperties,
        type: 'WrittenQuestion',
        typeToUse: 'WrittenQuestion',
        filtersProps: [{ field: 'questionByContactId', operator: 'str_eq', value: id }],
      });

      contentRelated.WrittenAnswer = await callRelatedContent({
        ...commonProperties,
        type: 'WrittenAnswer',
        typeToUse: 'WrittenAnswer',
        filtersProps: [{ field: 'answerByContactId', operator: 'str_eq', value: id }],
      });
    }
  };

  const callRelatedContent = async (props) => {
    try {
      const { type, content, pageNumber, freeSearch, topic, filtersProps, additionalFilters, typeToUse, sourceProp } =
        props;
      const listToApply = topic;
      const filters = filterFromType(type, content);
      let pathname = `/api/${
        !!listToApply?.id && enableStakeholders && !freeSearch
          ? 'search/query-by-keyword-list'
          : listToApply?.id === null && enableStakeholders && !freeSearch
            ? 'search/query-by-keyword-list-v2'
            : 'search/query'
      }`;
      let method = 'post';
      let requestProperties = {
        type: typeToUse ?? type,
        pageSize:
          contentType === undefined && changeContentType(contentType) !== type
            ? resourcesToShow
            : contentRelatedpageSize,
        filters,
        pageNumber: pageNumber ?? 1,
        sort: { field: 'dateTime', order: 'DESC' },
      };

      if (filtersProps) {
        requestProperties = {
          ...requestProperties,
          filters: filtersProps,
        };
      }

      if (additionalFilters) {
        requestProperties = {
          ...requestProperties,
          filters: [...filters, ...additionalFilters],
        };
      }

      let bigInt = true;
      let sort;
      let collapseField;
      let parliamentaryRecordItem =
        type === 'WrittenQuestion' || type === 'ParliamentaryRecord' || type === 'WrittenAnswer' || type === 'Edm';
      let needsAuthentication = isAuthenticated;

      if (type === 'ParliamentaryRecord') {
        collapseField = 'contentUrl';
      }
      if (parliamentaryRecordItem) {
        requestProperties = { ...requestProperties, doNotSearchTitle: true };
      }

      if (sort !== undefined) {
        requestProperties = { ...requestProperties, sort };
      }

      if (collapseField !== undefined) {
        requestProperties = {
          ...requestProperties,
          collapseField: collapseField,
          collapseSort: { field: 'contributionOrder', order: 'asc' },
        };
      }

      if (!!listToApply && enableStakeholders && !freeSearch) {
        requestProperties = {
          ...requestProperties,
          keywordListId: listToApply?.id,
        };
        if (stakeholderSearchCondition) {
          requestProperties = {
            ...requestProperties,
            includeExplanations: true,
          };
        }
      } else {
        requestProperties = {
          ...requestProperties,
          query: ChangeSearchQuery(freeSearch ?? ''),
        };
      }

      let searchApplied = activeSearch || freeSearch;
      let parliamentaryCondition = parliamentaryRecordItem && searchApplied;

      let keyUpdateHighlighted =
        type === 'KeyUpdate' && ((searchApplied && list !== null) || (searchApplied && listToApply === null));
      if (keyUpdateHighlighted || parliamentaryCondition) {
        requestProperties = {
          ...requestProperties,
          includeHighlights: true,
        };
      }
      let results = await generalApiCall({
        pathname,
        method,
        requestProperties,
        needsAuthentication,
        bigInt,
        requestSource: sourceProp,
        notShowErrorMessage: true,
      });
      if (results) {
        let hitsResults = pageNumber ? contentRelated[changeContentType(contentType)]?.hits : [];
        let explanations = pageNumber ? contentRelated[changeContentType(contentType)]?.explanations : [];
        let hitsHighlighted =
          results?.highlights?.length > 0
            ? results?.hits?.map((item, index) => {
                return {
                  ...item,
                  highlights: results?.highlights[index],
                };
              })
            : results.hits;
        hitsHighlighted = results.collapsedHits
          ? hitsHighlighted.map((item, index) => {
              return {
                ...item,
                collapsedHits: results.collapsedHits[index],
              };
            })
          : hitsHighlighted;

        hitsResults = hitsResults.concat(hitsHighlighted);
        explanations = explanations.concat(results.explanations);
        let numberOfResourcesToCompare = contentType ? contentRelatedpageSize : resourcesToShow;
        return {
          ...results,
          hits: hitsResults,
          explanations: explanations,
          thereIsMoreContent:
            results?.hits?.length === numberOfResourcesToCompare &&
            (!results?.totalHits || (!!results.totalHits && results?.totalHits > numberOfResourcesToCompare)),
        };
      }
    } catch (error) {}
  };

  return { stakeholderSearchCondition, callRelatedContent, relatedContentCall, contentTypeFilters };
};

export default useStakeholderContentRelated;
